// Plan with AI (shopping) — four entry points + a review pane.
//
//   Week:    pulls this week's meal plan, lists missing groceries
//   Recipes: pick recipes; aggregate ingredients minus what you have
//   Tidy:    sanity-check the current list (dedupe, missing pairs)
//   Free:    free-text request ("dinner party Saturday for 6")
//
// Each proposal carries a `reason` line for trust. The user accepts
// or dismisses each one (or hits "Accept all"). Accepted items get
// pushed to the active shopping list via the existing addItem RPC.

import { useEffect, useMemo, useState } from "preact/hooks";
import { Icon } from "../../icons";
import {
  api,
  type Recipe,
  type ShoppingList,
  type ShoppingPlanMode,
  type ShoppingProposal,
} from "../../lib/rpc";
import { ListPicker } from "./list-picker";

interface PlanWithAIProps {
  open: boolean;
  onClose: () => void;
  list: ShoppingList | null;
  notify: (msg: string) => void;
  /** Refresh the parent shopping view after accepting. */
  onApplied: () => Promise<void>;
}

type Stage = "setup" | "review";

const MODE_LABELS: Record<ShoppingPlanMode, { title: string; sub: string; icon: "calendar" | "flame" | "compact" | "sparkle" }> = {
  week: {
    title: "This week's meal plan",
    sub: "Pull groceries from the meals you've planned.",
    icon: "calendar",
  },
  recipes: {
    title: "From recipes",
    sub: "Pick recipes; we'll aggregate their ingredients.",
    icon: "flame",
  },
  tidy: {
    title: "Tidy the list",
    sub: "Dedupe near-duplicates, sanity-check quantities.",
    icon: "compact",
  },
  free: {
    title: "Free-text",
    sub: "Tell us what you need in plain language.",
    icon: "sparkle",
  },
};

export function PlanWithAI({ open, onClose, list, notify, onApplied }: PlanWithAIProps) {
  const [stage, setStage] = useState<Stage>("setup");
  const [mode, setMode] = useState<ShoppingPlanMode>("free");
  const [query, setQuery] = useState("");
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [recipeSearch, setRecipeSearch] = useState("");
  const [running, setRunning] = useState(false);
  const [proposals, setProposals] = useState<ShoppingProposal[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);
  // Picker state — opens when the user clicks Accept (all or single).
  // Pending indices is what the user actually accepted; the picker
  // routes them to whichever list they confirm.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingIndices, setPendingIndices] = useState<number[]>([]);
  // True when the most recent run() returned zero proposals (vs. when
  // the user worked through proposals to zero). Lets the review pane
  // show a different empty-state: "everything's already on the list"
  // vs. the generic "all handled, close to exit".
  const [initiallyEmpty, setInitiallyEmpty] = useState(false);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setStage("setup");
      setMode("free");
      setQuery("");
      setPicked(new Set());
      setRecipeSearch("");
      setRunning(false);
      setProposals([]);
      setDismissed(new Set());
      setApplying(false);
    }
  }, [open]);

  // Lazy-fetch recipes only when needed.
  useEffect(() => {
    if (!open || mode !== "recipes") return;
    if (recipes.length > 0) return;
    api.recipesList().then(setRecipes).catch(() => {});
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !list) return null;

  const filteredRecipes = useMemo(() => {
    const q = recipeSearch.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) => r.name.toLowerCase().includes(q));
  }, [recipes, recipeSearch]);

  const togglePick = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const canRun =
    mode === "free" ? query.trim().length > 0
    : mode === "recipes" ? picked.size > 0
    : true;

  const run = async () => {
    setRunning(true);
    setDismissed(new Set());
    setInitiallyEmpty(false);
    try {
      const r = await api.shoppingPlan({
        listId: list.id,
        mode,
        query: mode === "free" ? query.trim() : undefined,
        recipeIds: mode === "recipes" ? [...picked] : undefined,
      });
      setProposals(r.proposals);
      setInitiallyEmpty(r.proposals.length === 0);
      setStage("review");
    } catch (e: any) {
      notify(e?.message || "couldn't draft suggestions");
    } finally {
      setRunning(false);
    }
  };

  const accept = (which: "all" | number) => {
    if (!list) return;
    const indices = which === "all"
      ? proposals.map((_, i) => i).filter((i) => !dismissed.has(i))
      : [which];
    if (indices.length === 0) return;
    setPendingIndices(indices);
    setPickerOpen(true);
  };

  // Picker confirmed — actually push the items to the chosen list.
  const commitToList = async (listId: number, listName: string) => {
    setApplying(true);
    setPickerOpen(false);
    try {
      const indices = pendingIndices;
      const ops = indices.map((i) => {
        const p = proposals[i];
        return api.shoppingAddItem(listId, p.name, p.qty || undefined);
      });
      await Promise.all(ops);
      setProposals((prev) => prev.filter((_, i) => !indices.includes(i)));
      setDismissed((prev) => {
        const next = new Set<number>();
        prev.forEach((idx) => {
          let shift = 0;
          indices.forEach((removedIdx) => { if (removedIdx < idx) shift++; });
          if (!indices.includes(idx)) next.add(idx - shift);
        });
        return next;
      });
      await onApplied();
      notify(`Added ${ops.length} item${ops.length === 1 ? "" : "s"} to ${listName}`);
    } catch (e: any) {
      notify(e?.message || "couldn't add");
    } finally {
      setApplying(false);
      setPendingIndices([]);
    }
  };

  const remainingCount = proposals.length;

  return (
    // Fragment so ListPicker sits as a sibling of pwa-bg, escaping
    // its stacking context (same gotcha as RecipeDetail had — a
    // positioned ancestor with z-index clamps descendant z-indexes).
    <>
    <div className="pwa-bg" onClick={onClose}>
      <div className="pwa-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="pwa-head">
          {stage === "review" && (
            <button className="icon-btn" onClick={() => setStage("setup")} title="Back" type="button">
              <Icon name="arrow-left" size={13} />
            </button>
          )}
          <span className="pwa-head-icon"><Icon name="sparkle" size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pwa-head-title">
              {stage === "setup" ? "Plan with AI" : "Suggestions"}
            </div>
            <div className="pwa-head-sub">
              {stage === "setup" ? `For "${list.name}". Nothing's saved until you accept.`
                : `${remainingCount} idea${remainingCount === 1 ? "" : "s"} — accept the ones you want.`}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={13} />
          </button>
        </header>

        {stage === "setup" ? (
          <div className="pwa-body">
            <div className="pwa-mode-tiles">
              {(["week", "recipes", "tidy", "free"] as const).map((m) => (
                <button
                  key={m}
                  className="pwa-mode-tile"
                  data-active={mode === m}
                  onClick={() => setMode(m)}
                  type="button"
                >
                  <span className="pwa-mode-icon"><Icon name={MODE_LABELS[m].icon} size={14} /></span>
                  <span className="pwa-mode-name">{MODE_LABELS[m].title}</span>
                  <span className="pwa-mode-sub">{MODE_LABELS[m].sub}</span>
                </button>
              ))}
            </div>

            {mode === "free" && (
              <div className="pwa-field">
                <label>Tell us what you need</label>
                <textarea
                  className="am-input"
                  rows={3}
                  placeholder='e.g. "dinner party Saturday for 6, vegetarian for two guests, light app + main + dessert"'
                  value={query}
                  onInput={(e) => setQuery((e.currentTarget as HTMLTextAreaElement).value)}
                  maxLength={500}
                  autofocus
                />
              </div>
            )}

            {mode === "recipes" && (
              <div className="pwa-field">
                <label>Pick recipes</label>
                <input
                  type="text"
                  className="am-input"
                  placeholder="Filter recipes…"
                  value={recipeSearch}
                  onInput={(e) => setRecipeSearch((e.currentTarget as HTMLInputElement).value)}
                  style={{ marginBottom: 6 }}
                />
                <div className="pwa-recipe-list">
                  {filteredRecipes.length === 0
                    ? <p className="pwa-empty">
                        {recipes.length === 0 ? "No recipes saved yet." : "No matches."}
                      </p>
                    : filteredRecipes.map((r) => {
                        const checked = picked.has(r.id);
                        return (
                          <button
                            key={r.id}
                            className="pwa-recipe-row"
                            data-active={checked}
                            onClick={() => togglePick(r.id)}
                            type="button"
                          >
                            <span className="pwa-check" data-on={checked}>
                              <Icon name="check" size={11} />
                            </span>
                            <span style={{ flex: 1 }}>{r.name}</span>
                          </button>
                        );
                      })
                  }
                </div>
              </div>
            )}

            {mode === "week" && (
              <p className="pwa-info">
                We'll look at this week's meal plan (Mon–Sun) and list ingredients you don't have yet.
              </p>
            )}

            {mode === "tidy" && (
              <p className="pwa-info">
                We'll review the current "{list.name}" list and suggest tidying — duplicates, missing pairs, sanity-checked quantities.
              </p>
            )}

            <div className="am-actions">
              <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn btn-primary"
                onClick={run}
                disabled={!canRun || running}
                type="button"
              >
                <Icon name="sparkle" size={13} />
                <span>{running ? "Thinking…" : "Get suggestions"}</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="pwa-body">
            {proposals.length === 0
              ? (initiallyEmpty
                  ? (
                    <div className="pwa-empty pwa-empty-covered">
                      <Icon name="check" size={20} />
                      <p>
                        <strong>You're covered.</strong><br />
                        Every ingredient from {mode === "week" ? "this week's meal plan" : mode === "recipes" ? "the recipes you picked" : "your request"} is already on the "{list?.name}" list.
                      </p>
                    </div>
                  )
                  : <p className="pwa-empty">All suggestions handled. Close or go back to draft more.</p>)
              : (
                <div className="pwa-proposals">
                  {proposals.map((p, i) => {
                    if (dismissed.has(i)) return null;
                    return (
                      <div className="pwa-prop" key={`${p.name}-${i}`}>
                        <div className="pwa-prop-main">
                          <span className="pwa-prop-name">{p.name}</span>
                          {p.qty && <span className="pwa-prop-qty">{p.qty}</span>}
                        </div>
                        {p.reason && (
                          <p className="pwa-prop-reason">{p.reason}</p>
                        )}
                        <div className="pwa-prop-actions">
                          <button
                            className="pwa-prop-x"
                            onClick={() => setDismissed((prev) => new Set(prev).add(i))}
                            title="Dismiss"
                            type="button"
                          >
                            <Icon name="x" size={12} />
                          </button>
                          <button
                            className="pwa-prop-add"
                            onClick={() => accept(i)}
                            disabled={applying}
                            title="Accept"
                            type="button"
                          >
                            <Icon name="plus" size={12} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }

            <div className="pwa-review-actions">
              <button className="btn-ghost" onClick={onClose} type="button">Done</button>
              <span style={{ flex: 1 }} />
              <button
                className="btn"
                onClick={run}
                disabled={running || applying}
                type="button"
              >
                <Icon name="sparkle" size={13} />
                <span>{running ? "…" : "Regenerate"}</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => accept("all")}
                disabled={applying || proposals.filter((_, i) => !dismissed.has(i)).length === 0}
                type="button"
              >
                <Icon name="check" size={13} />
                <span>
                  {applying ? "Adding…" : `Accept all (${proposals.filter((_, i) => !dismissed.has(i)).length})`}
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    <ListPicker
      open={pickerOpen}
      defaultListId={list?.id ?? null}
      onClose={() => { setPickerOpen(false); setPendingIndices([]); }}
      onPick={commitToList}
      notify={notify}
    />
    </>
  );
}
